// ============================================================
//  AI 助手「历史净化」纯逻辑（无框架、无副作用）
//  契约：.scratch/ai-chat-assistant/spec.md §6.6
//
//  为什么需要：多轮会话是**无状态**的——前端把历史随每轮请求带给后端。
//  若把上一轮的 PII 卡片原值（电话/住址）原样回灌，号码会反复进入模型上下文，
//  既有重复暴露风险，也可能被模型转述出错。因此发送前必须净化：
//    · contact 卡（PII 原值）→ 占位符文本「[已展示：张伟 联系电话卡片]」
//    · record / students 卡与助手文字 → 保留（不含 PII 原值）
//    · 截断到最近 12 条、首条必须是 user、同角色相邻消息合并（保持交替）
//  用户说"再发一次那个电话"时，模型看不到原值 → 会重新调 T2 查库（后端负责）。
// ============================================================

import { AI_CHAT_CARD_CONTACT, type AiChatCard } from "./ai-chat-cards";

/** 页面内存里的会话消息（assistant 可以只有卡片没有文字） */
export interface AiChatSessionMessage {
  role: "user" | "assistant";
  content: string;
  cards?: readonly AiChatCard[];
}

/** 发给后端的消息（净化后，绝不含 PII 原值） */
export interface AiChatOutgoingMessage {
  role: "user" | "assistant";
  content: string;
}

/** 随请求携带的历史上限（spec §6.6；后端还会再截一次） */
export const AI_CHAT_HISTORY_LIMIT = 12;

/**
 * 净化历史（不含本轮新问题）：把 assistant 的 contact 卡换成占位符，
 * 丢弃空消息，按上限截断并规整为交替角色。
 */
export function purifyHistory(
  messages: readonly AiChatSessionMessage[],
): AiChatOutgoingMessage[] {
  const mapped: AiChatOutgoingMessage[] = [];
  for (const message of messages) {
    const purified = purifyOne(message);
    if (purified) mapped.push(purified);
  }
  return normalize(mapped, AI_CHAT_HISTORY_LIMIT);
}

/**
 * 组装本轮请求消息 = 净化后的历史 + 本轮提问。
 * 保证：末条是本轮 user 提问；超出上限时优先保留最近的消息。
 */
export function buildAskMessages(
  history: readonly AiChatSessionMessage[],
  question: string,
): AiChatOutgoingMessage[] {
  const outgoing = purifyHistory(history);
  const trimmed = question.trim();
  if (trimmed.length > 0) outgoing.push({ role: "user", content: trimmed });
  return normalize(outgoing, AI_CHAT_HISTORY_LIMIT);
}

/** contact 卡的占位符文案（原值绝不进入这里） */
export function contactCardPlaceholder(card: AiChatCard): string {
  const name = (card.studentName ?? "").trim();
  const labels = collectFieldLabels(card);
  const who = name.length > 0 ? name : "该学生";
  const what = labels.length > 0 ? labels.join("、") : "联系方式";
  return `[已展示：${who} ${what}卡片]`;
}

/** 单条消息净化；净化后为空返回 null（该条不发送） */
function purifyOne(message: AiChatSessionMessage): AiChatOutgoingMessage | null {
  const text = (message.content ?? "").trim();
  if (message.role === "user") {
    return text.length > 0 ? { role: "user", content: text } : null;
  }

  const placeholders = (message.cards ?? [])
    .filter((card) => card.type === AI_CHAT_CARD_CONTACT)
    .map((card) => contactCardPlaceholder(card));

  const parts = text.length > 0 ? [text, ...placeholders] : placeholders;
  const content = parts.join("\n").trim();
  return content.length > 0 ? { role: "assistant", content } : null;
}

/** 字段标签归并：电话类合成「联系电话」，住址「家庭住址」，宿舍「宿舍」，其余保留原标签 */
function collectFieldLabels(card: AiChatCard): string[] {
  const labels: string[] = [];
  const push = (value: string): void => {
    if (value.length > 0 && !labels.includes(value)) labels.push(value);
  };

  let hasPhone = false;
  for (const field of card.fields ?? []) {
    if (field.key === "fatherPhone" || field.key === "motherPhone" || field.key === "dailyContactPhone") {
      hasPhone = true;
      continue;
    }
    if (field.key === "address") {
      push("家庭住址");
      continue;
    }
    if (field.key === "dormitory") {
      push("宿舍");
      continue;
    }
    push(field.label);
  }
  if (hasPhone) labels.unshift("联系电话");
  return labels;
}

/**
 * 规整为「最近的 limit 条、首条 user、角色交替」：
 *  ① 只保留尾部 limit 条（优先丢最早的历史）
 *  ② 丢掉开头连续的 assistant（模型不该以助手发言起手）
 *  ③ 相邻同角色合并（避免后端/模型对连续同角色产生歧义）
 */
function normalize(
  messages: readonly AiChatOutgoingMessage[],
  limit: number,
): AiChatOutgoingMessage[] {
  let list = messages.filter((message) => message.content.length > 0);
  if (limit > 0 && list.length > limit) list = list.slice(list.length - limit);

  while (list.length > 0 && list[0]?.role === "assistant") list = list.slice(1);

  const merged: AiChatOutgoingMessage[] = [];
  for (const message of list) {
    const last = merged[merged.length - 1];
    if (last && last.role === message.role) {
      last.content = `${last.content}\n${message.content}`;
      continue;
    }
    merged.push({ role: message.role, content: message.content });
  }
  return merged;
}
